import { useEffect } from 'preact/hooks';
import { metricsApi } from '@/lib/api/endpoints';
import { refreshMounted } from '@/stores/queryCache';

/** Cada cuánto se pregunta si han cambiado los datos (solo con la pestaña visible). */
export const DATA_VERSION_INTERVAL_MS = 30_000;

/** `undefined` = no se pudo leer (no cuenta como cambio); `null` = aún sin datos. */
type VersionReader = () => Promise<string | null | undefined>;

/**
 * Comprobador de versión: avisa solo cuando la versión CAMBIA respecto a la
 * última leída. La primera lectura fija la referencia sin avisar.
 */
export function createDataVersionWatcher(read: VersionReader, onChange: () => void) {
  let last: string | null | undefined;
  return async function check(): Promise<void> {
    const version = await read();
    if (version === undefined) return;
    if (last !== undefined && version !== last) onChange();
    last = version;
  };
}

async function readVersion(): Promise<string | null | undefined> {
  try {
    const { data, error } = await metricsApi.version();
    if (error) return undefined;
    return data?.version ?? null;
  } catch {
    return undefined;
  }
}

/**
 * Las pantallas se refrescan solas cuando cambian los datos del servidor: fin
 * de un sync, cifras recalculadas, trazas nuevas. Antes había que pulsar
 * "Actualizar datos" o recargar (petición del usuario, 2026-09-30).
 */
export function useDataVersionWatcher(): void {
  useEffect(() => {
    const check = createDataVersionWatcher(readVersion, refreshMounted);
    const visible = () => document.visibilityState === 'visible';
    const tick = () => {
      if (visible()) void check();
    };
    tick();
    const timer = window.setInterval(tick, DATA_VERSION_INTERVAL_MS);
    // Al volver a la pestaña, comprobar en el acto en vez de esperar al intervalo.
    document.addEventListener('visibilitychange', tick);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, []);
}
